import type { Post } from "@/lib/sanity";
import { imageBuilder } from "@/lib/sanity";
import { cn } from "@/lib/utils";

type ArticleItemProps = {
  post: Post;
  onNavigate: (path: string) => void;
  className?: string;
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ArticleItem({ post, onNavigate, className }: ArticleItemProps) {
  const slug = post.slug?.current ?? "";
  const categorySlug = post.categories?.[0]?.slug?.current;
  const href = categorySlug ? `/articles/${categorySlug}/${slug}` : `/articles/${slug}`;
  const imageUrl = imageBuilder(post.mainImage, 800);

  return (
    <article
      className={cn(
        "group flex flex-col overflow-hidden rounded-lg border border-border/60 bg-card transition-colors hover:border-foreground/20 hover:shadow-sm",
        className,
      )}
    >
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          onNavigate(href);
        }}
        className="flex flex-1 flex-col"
      >
        {imageUrl ? (
          <div className="aspect-video w-full overflow-hidden bg-muted">
            <img
              src={imageUrl}
              alt={post.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-muted/60" aria-hidden />
        )}
        <div className="flex flex-1 flex-col p-5">
          <time
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground"
            dateTime={post.publishedAt ?? undefined}
          >
            {formatDate(post.publishedAt)}
          </time>
          <h2 className="mt-2 font-serif text-xl font-semibold leading-snug text-foreground group-hover:underline">
            {post.title}
          </h2>
          {post.excerpt ? (
            <p className="mt-2 line-clamp-3 text-sm text-muted-foreground leading-relaxed">
              {post.excerpt}
            </p>
          ) : null}
          <span className="mt-4 inline-block text-sm font-medium text-foreground/80 group-hover:underline">
            Read more →
          </span>
        </div>
      </a>
    </article>
  );
}
